import type {
  ProfileExperienceConfig,
  ProfileFieldConfig,
  RoleProfileConfig,
} from '../types/profile'

/**
 * The profile's field register.
 *
 * Adding a field here makes it appear in About you, makes it editable, and
 * makes it count toward completion — with no component edit. Labels and
 * purposes are terminology templates, resolved at render.
 */
export const PROFILE_FIELDS: ProfileFieldConfig[] = [
  {
    id: 'bio',
    label: 'Bio',
    purpose: 'A short line about you, shown according to your profile visibility.',
    kind: 'longtext',
    icon: 'bio',
    required: true,
    editable: true,
    roles: [],
    emptyLabel: 'Add a short bio',
    placeholder: 'What are you learning, building or looking for?',
  },
  {
    id: 'location',
    label: 'Location',
    purpose: 'Used to order results by distance. Never an exact address.',
    kind: 'text',
    icon: 'location',
    required: false,
    editable: true,
    roles: [],
    emptyLabel: 'Add your location',
    placeholder: 'City, state',
  },
  {
    id: 'interests',
    label: 'Interests',
    purpose: 'Improves the {community} and {opportunity:plural} suggested to you.',
    kind: 'tags',
    icon: 'interests',
    required: true,
    editable: true,
    roles: [],
    emptyLabel: 'Add your interests',
    placeholder: 'Technology, Music, Entrepreneurship',
  },
  {
    id: 'skills',
    label: 'Skills',
    purpose: 'Shown to verified {employer:plural} only when your career profile allows it.',
    kind: 'tags',
    icon: 'skills',
    required: true,
    editable: true,
    roles: [],
    emptyLabel: 'Add your skills',
    placeholder: 'React, Data analysis, Design',
  },
  {
    id: 'lookingFor',
    label: 'Looking for',
    purpose: 'What you want {platform} to surface — roles, projects or events.',
    kind: 'tags',
    icon: 'lookingFor',
    required: false,
    editable: true,
    roles: [],
    emptyLabel: 'Say what you are looking for',
    placeholder: 'Internships, Hackathons, Part-time roles',
  },
  {
    id: 'languages',
    label: 'Languages',
    purpose: 'Helps with {roommate} matching and study groups.',
    kind: 'tags',
    icon: 'languages',
    required: false,
    editable: true,
    roles: [],
    emptyLabel: 'Add the languages you speak',
    placeholder: 'English, Yoruba',
  },
]

/**
 * The destinations the profile hands off to. Nothing is duplicated here — each
 * row opens the surface that actually owns those settings, so there is one place
 * a preference lives.
 */
export const PROFILE_EXPERIENCE: ProfileExperienceConfig[] = [
  {
    id: 'housing',
    label: '{housing} preferences',
    description: 'Manage your accommodation needs and {roommate} preferences.',
    icon: 'housing',
    to: '/housing/roommates',
    roles: [],
  },
  {
    id: 'opportunities',
    label: '{opportunity} preferences',
    description: 'Set what you want to be considered for.',
    icon: 'opportunities',
    to: '/opportunities',
    roles: [],
  },
  {
    id: 'marketplace',
    label: '{marketplace} preferences',
    description: 'Manage your buying and selling preferences.',
    icon: 'marketplace',
    to: '/marketplace',
    roles: [],
  },
  {
    id: 'academic',
    label: 'Academic preferences',
    description: 'Manage your registration, timetable and results.',
    icon: 'academic',
    to: '/academics',
    roles: [],
  },
  {
    id: 'notifications',
    label: 'Notification preferences',
    description: 'Choose how you want to be notified.',
    icon: 'notifications',
    to: '/settings',
    roles: [],
  },
]

/**
 * Role-aware profiles. The base profile is shared; a role adds its own console
 * rather than a duplicate profile page. A role a person does not hold is shown
 * as unavailable rather than hidden, so the platform's shape is legible — but it
 * cannot be entered, because the roles on the identity decide that.
 */
export const ROLE_PROFILES: RoleProfileConfig[] = [
  {
    role: 'student',
    label: '{student}',
    summary: 'Manage your academic journey and campus life.',
    icon: 'academic',
    to: '/',
  },
  {
    role: 'property_manager',
    label: '{property} provider',
    summary: 'Manage your {property:plural}, tenants and occupancy.',
    icon: 'housing',
    to: '/property',
  },
  {
    role: 'service_provider',
    label: '{marketplace} vendor',
    summary: 'Manage your store, packages and orders.',
    icon: 'marketplace',
    to: '/provider',
  },
  {
    role: 'university_admin',
    label: '{campus} administrator',
    summary: 'Manage your {institution}, departments and permissions.',
    icon: 'campus',
    to: '/institution',
  },
  {
    role: 'employer',
    label: '{employer}',
    summary: 'Manage your listings, candidates and campaigns.',
    icon: 'opportunities',
    to: '/employer',
  },
  {
    role: 'campus_representative',
    label: '{representative}',
    summary: 'Manage your tasks, evidence and earnings.',
    icon: 'people',
    to: '/representative',
  },
]
